import React, { createContext, useContext, useMemo, useState } from "react";
import { enUS, es, fr, ptBR, de } from "date-fns/locale";
import { PHASES, PHASE_ORDER, WATER_ARTICLE, PROTEIN_ARTICLE } from "../lib/cycle";
import en from "./en";
import esTr from "./translations/es";
import frTr from "./translations/fr";
import ptTr from "./translations/pt";
import deTr from "./translations/de";

export const LANGS = [
  { code: "en", name: "English" },
  { code: "es", name: "Español" },
  { code: "fr", name: "Français" },
  { code: "pt", name: "Português" },
  { code: "de", name: "Deutsch" },
];

const TR = { es: esTr, fr: frTr, pt: ptTr, de: deTr };
const DATE_LOCALES = { en: enUS, es, fr, pt: ptBR, de };
const LANG_KEY = "cycleSync.lang";

export function detectLang() {
  try {
    const saved = localStorage.getItem(LANG_KEY);
    if (saved && (saved === "en" || TR[saved])) return saved;
  } catch (e) {
    /* ignore */
  }
  const nav = (navigator.language || "en").slice(0, 2).toLowerCase();
  return nav === "en" || TR[nav] ? nav : "en";
}

function mergeArticle(enArt, trArt) {
  if (!trArt) return enArt;
  return {
    ...enArt,
    title: trArt.title ?? enArt.title,
    readTime: trArt.readTime ?? enArt.readTime,
    blocks: trArt.blocks ?? enArt.blocks,
    references: trArt.references ?? enArt.references,
  };
}

function mergePhase(enP, trP) {
  if (!trP) return enP;
  return {
    ...enP,
    label: trP.label ?? enP.label,
    short: trP.short ?? enP.short,
    hormone: trP.hormone ?? enP.hormone,
    proteinNote: trP.proteinNote ?? enP.proteinNote,
    activity: { ...enP.activity, note: trP.activityNote ?? enP.activity.note },
    carbs: {
      ...enP.carbs,
      note: trP.carbsNote ?? enP.carbs.note,
      alt: enP.carbs.alt
        ? { ...enP.carbs.alt, note: trP.carbsAltNote ?? enP.carbs.alt.note }
        : undefined,
    },
    fat: { ...enP.fat, note: trP.fatNote ?? enP.fat.note },
    tips: trP.tips ?? enP.tips,
    article: mergeArticle(enP.article, trP.article),
  };
}

export function buildContent(lang) {
  const tr = TR[lang];
  const phases = {};
  PHASE_ORDER.forEach((k) => {
    phases[k] = tr?.phases?.[k] ? mergePhase(PHASES[k], tr.phases[k]) : PHASES[k];
  });
  return {
    phases,
    water: mergeArticle(WATER_ARTICLE, tr?.water),
    protein: mergeArticle(PROTEIN_ARTICLE, tr?.protein),
    ui: { ...en.ui, ...(tr?.ui || {}) },
  };
}

function interpolate(str, vars) {
  if (!vars || typeof str !== "string") return str;
  return str.replace(/\{(\w+)\}/g, (_, k) => (vars[k] != null ? vars[k] : `{${k}}`));
}

const I18nContext = createContext(null);

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(detectLang);

  const setLang = (l) => {
    try {
      localStorage.setItem(LANG_KEY, l);
    } catch (e) {
      /* ignore */
    }
    if (typeof document !== "undefined") document.documentElement.lang = l;
    setLangState(l);
  };

  const content = useMemo(() => buildContent(lang), [lang]);
  const t = (key, vars) => interpolate(content.ui[key] ?? key, vars);
  const dateLocale = DATE_LOCALES[lang] || enUS;

  const value = {
    lang,
    setLang,
    langs: LANGS,
    t,
    dateLocale,
    weekdays: content.ui.weekdays,
    ...content,
  };

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used within I18nProvider");
  return ctx;
}
